import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { path } from "../data/base";
import type { MachineProposal } from "../data/cockpit-data";

/**
 * The reviewer's scratchpad — one note per proposal.
 *
 * Reference only: decisions and their reasons live on the approval audit trail,
 * and a note is not one. Save stays a SECONDARY button because the single
 * primary on a proposal card is reserved for Approve.
 *
 * The write goes through `path()` like every other call in this module. Writing
 * `/api/machine/note` here would post to the PORTAL's own origin and 404 in the
 * one way that looks like a broken feature rather than a wrong URL.
 */
export function NotesBox({ proposal, onSaved }: { proposal: MachineProposal; onSaved?: () => void }) {
  const [text, setText] = useState(proposal.operator_note ?? "");
  const [savedText, setSavedText] = useState(proposal.operator_note ?? "");
  const [savedBy, setSavedBy] = useState(proposal.note_updated_by);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = text !== savedText;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(path("/note"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ proposal_id: proposal.proposal_id, note: text }),
      });
      // 403 is upstream's answer, not a rule this module keeps a copy of.
      if (response.status === 403) {
        setError("You don't have note authority.");
        return;
      }
      if (!response.ok) {
        setError("Save failed. Retry.");
        return;
      }
      setSavedText(text);
      setSavedBy("you");
      onSaved?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-ks-grey-3 bg-muted/40 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your notes</p>
      <Textarea
        className="mt-2 min-h-16"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Reference notes for this proposal (only visible in the cockpit)"
        maxLength={4000}
        disabled={busy}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={save} disabled={busy || !dirty}>
          {busy ? "Saving…" : dirty ? "Save note" : "Saved"}
        </Button>
        {!dirty && savedText && savedBy && (
          <span className="text-xs text-muted-foreground">Last saved by {savedBy}</span>
        )}
        {error && (
          <span className="text-xs font-medium text-ks-danger" role="status">
            {error}
          </span>
        )}
      </div>
    </section>
  );
}
